import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-hot-toast";

import Button, {
  ButtonSize,
  ButtonVariant,
} from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import Toast3 from "../../../components/Toast3";
import { PATHS } from "../../../config/paths";
import { getProfileQueryKey } from "../../../shared/hooks/useProfile";
import { useAuthDetails } from "../hooks/useAuthDetails";
import { SettingsService } from "../services/settings.service";
import { UpdateProfileDto, User } from "../types/settings.types";
import ImageUpload from "./ImageUpload";
import PasswordForm from "./PasswordForm";

interface ProfileFormProps {
  user: User;
}

export default function ProfileForm({
  user: { profile, email, settings },
}: ProfileFormProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateProfileDto>({
    profile: {
      ...profile,
      summary: profile?.summary || "",
      location: profile?.location || "",
      title: profile?.title || "",
      name: profile?.name || "",
      aboutMe: profile?.aboutMe || "",
    },
    settings,
  });

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const { authDetails, isLoading: isLoadingAuth } = useAuthDetails();

  // Update profile mutation
  const { mutate: updateProfile, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdateProfileDto) => SettingsService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
      toast.success("Profile updated successfully");
    },
    onError: () => {
      toast.error("Failed to update profile");
    },
  });

  // Upload image mutation
  const { mutate: uploadImage, isPending: isUploading } = useMutation({
    mutationFn: (file: File) => SettingsService.uploadProfileImage(file),
    onSuccess: (response) => {
      setFormData((prev) => ({
        ...response,
      }));

      queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
      toast.success("Profile image updated successfully");
    },
    onError: () => {
      toast.error("Failed to upload image");
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        [name]: value,
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(formData);
  };

  const triggerSubmit = () => {
    updateProfile(formData);
  };

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className={`text-2xl ${theme.text.primary} font-bold`}>
            My Account
          </h2>
          <Button
            as="navlink"
            to={PATHS.COMMUNITY.MY_PROFILE}
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.SMALL}
          >
            View Profile
          </Button>
        </div>
        {/* Image */}
        <section>
          <ImageUpload
            currentImageUrl={profile.thumbnailUrl}
            onImageSelect={uploadImage}
            isUploading={isUploading}
          />
        </section>

        <form onSubmit={handleSubmit}>
          <section>
            <h2
              className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
            >
              Profile
            </h2>
            <div className="text-sm">
              You can update your profile information here.
            </div>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* Name */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="name"
                >
                  Name <span className="text-rose-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.name || ""}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Email
                </label>
                <input
                  id="email"
                  className="form-input w-full dark:disabled:placeholder:text-gray-600 disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed shadow-none"
                  type="text"
                  placeholder="Email"
                  value={email}
                  disabled
                />
              </div>
            </div>
            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* Summary */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="summary"
                >
                  Summary
                </label>
                <input
                  id="summary"
                  name="summary"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.summary || ""}
                  onChange={handleChange}
                />
              </div>

              {/* Title */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="title"
                >
                  Title
                </label>
                <input
                  id="title"
                  name="title"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.title || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* Location */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="location"
                >
                  Location
                </label>
                <input
                  id="location"
                  name="location"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.location || ""}
                  onChange={handleChange}
                />
              </div>

              {/* Birthday */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="birthday"
                >
                  Birthday
                </label>
                <input
                  id="birthday"
                  name="birthday"
                  className="form-input w-full"
                  type="date"
                  value={formData.profile.birthday}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* About Me */}
              <div className="w-full">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="aboutMe"
                >
                  About Me
                </label>
                <textarea
                  id="aboutMe"
                  name="aboutMe"
                  className="form-textarea w-full"
                  rows={4}
                  value={formData.profile.aboutMe}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>
        </form>

        {/* Password */}
        <section>
          <h2
            className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
          >
            Password
          </h2>
          <div className="text-sm">
            {authDetails?.provider === "google" ? (
              <Toast3
                open={true}
                setOpen={() => {}}
                className={undefined}
                type={undefined}
                closable={false}
              >
                You signed up with Google. You can't set up a password.
              </Toast3>
            ) : (
              <span>You can change your password here.</span>
            )}
          </div>
          {authDetails?.provider !== "google" ? (
            <div className="mt-5">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setPasswordModalOpen(true);
                }}
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SMALL}
              >
                {authDetails?.hasPassword ? "Change Password" : "Set Password"}
              </Button>

              <PasswordForm
                modalOpen={passwordModalOpen}
                setModalOpen={setPasswordModalOpen}
                isGoogleUser={false}
                hasPassword={authDetails?.hasPassword}
              />
            </div>
          ) : null}
        </section>
      </div>
      {/* Panel footer */}
      <footer>
        <div
          className={`flex flex-col px-6 py-5 border-t ${theme.border.primary}`}
        >
          <div className="flex self-end">
            <Button type="submit" disabled={isUpdating} onClick={triggerSubmit}>
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
